import {
  CustomModalActionType,
  FormBaseType,
  FormQuestionDto,
} from "@green0meter/api-client";
import React, { FC, memo, useEffect, useMemo, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { GetInTouchModal } from "@components/common/Modals";
import {
  doesAnswerTriggerModal,
  useMultistepFormStore,
} from "@components/common/MultistepForm";
import { useUserStore } from "@store/user";
import {
  useCreateUpdateDraftData,
  useGetCurrentCompanyInfo,
  useGetFormDraftData,
} from "@api";

interface ConditionalModal {
  question: FormQuestionDto;
}

// TODO: allow only one modal per question? investigate
const ConditionalModal: FC<ConditionalModal> = memo(({ question }) => {
  const watchedValue: string = useWatch({ name: question.code });
  const { setValue } = useFormContext();
  const { user } = useUserStore();
  const { currentCompanyInfo } = useGetCurrentCompanyInfo(user);
  const formId = useMultistepFormStore((s) => s.formSettings.formBasics.id);
  const formBaseType = useMultistepFormStore(
    (store) => store.formSettings.formBasics.formBaseType,
  );
  const { formDraftData } = useGetFormDraftData(user, formId, false);
  const { createUpdateDraftData } = useCreateUpdateDraftData(user, formId);

  const [open, setOpen] = useState(false);

  const hasBeenModalTriggered =
    formDraftData?.some(
      (item) =>
        item.questionCode === question.code &&
        item.wasGetInTouchModalTriggered === true,
    ) ?? false;

  const modalData = useMemo(
    () =>
      question.questionConditionalModals?.find(
        (m) => m.actionType === CustomModalActionType.GetInTouch,
      ),
    [question, watchedValue],
  );

  const modalMatch = useMemo(
    () =>
      !!modalData &&
      doesAnswerTriggerModal(
        question,
        { [question.code]: watchedValue },
        modalData,
        currentCompanyInfo,
      ),
    [question, watchedValue, currentCompanyInfo, modalData],
  );

  const isCo2Form = formBaseType === FormBaseType.Co2;
  const isUserCo2NonPremium = isCo2Form && !currentCompanyInfo?.hasFullPremium;

  const nonTriggeringOptionCode = useMemo(() => {
    if (!modalData?.values) return undefined;
    const triggerValues = new Set(modalData.values);

    return question.answerOptions?.find((opt) => !triggerValues.has(opt.code))?.code;
  }, [modalData?.values, question.answerOptions]);

  useEffect(() => {
    // Banner disabled for premium users
    if (isCo2Form && currentCompanyInfo?.hasFullPremium) return;

    if (modalMatch && !hasBeenModalTriggered) {
      setOpen(true);

      if (isUserCo2NonPremium && nonTriggeringOptionCode) {
        setValue(question.code, nonTriggeringOptionCode);
      }
    }
  }, [modalMatch, hasBeenModalTriggered, watchedValue]);

  useEffect(() => {
    const hasSomeAnswer =
      watchedValue !== undefined && watchedValue !== null && watchedValue !== "";

    if (!modalMatch && hasBeenModalTriggered && hasSomeAnswer) {
      createUpdateDraftData({
        formId,
        questionCode: question.code,
        wasGetInTouchModalTriggered: false,
      });
    }
  }, [
    modalMatch,
    hasBeenModalTriggered,
    watchedValue,
    formId,
    question.code,
    createUpdateDraftData,
  ]);

  const handleConfirm = (): void => {
    if (!isUserCo2NonPremium) {
      createUpdateDraftData({
        formId,
        questionCode: question.code,
        wasGetInTouchModalTriggered: true,
      });
    }

    setOpen(false);
  };

  if (!modalData) return null;

  return (
    <GetInTouchModal
      open={open}
      data={modalData}
      onClose={() => setOpen(false)}
      onConfirmed={handleConfirm}
    />
  );
});

export default ConditionalModal;
